/* One public station (operators): its details, its programmes (the base format plus named ones, each with a
   brief for the show runner and a genre mix) and the weekly grid that places them. */
import { useParams } from '@solidjs/router';
import { For, Show, createEffect, createResource, createSignal } from 'solid-js';
import { api, DJ, Mode, Programme, Station } from '../api';
import { ConfirmButton, ErrorLine, PageHeader, StatusPill, useAction } from '../ui';
import GridEditor, { colourOf } from '../../../shared/GridEditor';
import RunningOrder from './RunningOrder';
import Shouts from './Shouts';
import MixEditor, { GenreWeight } from '../../../shared/MixEditor';
import { untilLabel } from './Radio';

const TALK = { none: 'No talk', low: 'A little (every 4–5 tracks)', normal: 'Normal (every 2–3 tracks)', more: 'More (time checks, every 1–2 tracks)' };
const MODE: Record<Mode, string> = { auto: 'Auto: songs only', dj: 'DJ: songs and links', voice: 'Voice: a presenter over the music',
  live: 'Live: a live input' };
const SHORT: Record<Mode, string> = { auto: 'Auto', dj: 'DJ', voice: 'Voice', live: 'Live' };
const SOURCE = { mix: 'Its genre mix', new: 'Songs added in the past week', most_played: "The week's most replayed songs" };
const slugOf = (name: string) => name.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/^[^a-z]+/, '').slice(0, 32);
const sameMix = (a: GenreWeight[], b: GenreWeight[]) =>
  JSON.stringify([...a].sort((x, y) => x.genre.localeCompare(y.genre))) === JSON.stringify([...b].sort((x, y) => x.genre.localeCompare(y.genre)));

function StationDetails(props: { station: Station; djs: DJ[]; onSaved: (s: Station) => void }) {
  const [f, setF] = createSignal({ name: '', description: '', dj_id: '', mode: 'dj' as Mode, crossfade: 2, adverts: false, top_of_hour: true,
    no_repeat_hours: 24 });
  const a = useAction();
  const reset = () => setF({ name: props.station.name, description: props.station.description ?? '', dj_id: props.station.dj?.id ?? '',
    mode: props.station.mode, crossfade: props.station.crossfade_ms / 1000, ...props.station.format });
  createEffect(reset);
  const save = (e: Event) => {
    e.preventDefault();
    const v = f();
    a.run(async () => props.onSaved(await api<Station>(`/radio/stations/${props.station.id}`, { method: 'PATCH',
      json: { name: v.name, description: v.description.trim() || null, dj_id: v.dj_id || undefined, mode: v.mode, crossfade_ms: Math.round(v.crossfade * 1000),
        format: { adverts: v.adverts, top_of_hour: v.top_of_hour, no_repeat_hours: v.no_repeat_hours } } })));
  };
  return (
    <form class="grid gap-3 sm:grid-cols-2" onSubmit={save}>
      <label class="form-control"><span class="label-text font-semibold">Name</span>
        <input class="input input-bordered" required value={f().name} onInput={(e) => setF({ ...f(), name: e.currentTarget.value })} /></label>
      <label class="form-control"><span class="label-text font-semibold">DJ</span>
        <select class="select select-bordered" value={f().dj_id} onChange={(e) => setF({ ...f(), dj_id: e.currentTarget.value })}>
          <Show when={!props.station.dj}><option value="">No host</option></Show>
          <For each={props.djs}>{(d) => <option value={d.id}>{d.display_name}</option>}</For>
        </select></label>
      <label class="form-control"><span class="label-text font-semibold">Mode (unless a programme has its own)</span>
        <select class="select select-bordered" value={f().mode} onChange={(e) => setF({ ...f(), mode: e.currentTarget.value as Mode })}>
          <For each={Object.entries(MODE)}>{([k, l]) => <option value={k}>{l}</option>}</For>
        </select></label>
      <label class="form-control sm:col-span-2"><span class="label-text font-semibold">Description (public)</span>
        <textarea class="textarea textarea-bordered" rows="2" maxLength={500} value={f().description}
          onInput={(e) => setF({ ...f(), description: e.currentTarget.value })} /></label>
      <div class="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm sm:col-span-2">
        <label class="flex items-center gap-2"><input type="checkbox" class="toggle toggle-primary toggle-sm" checked={f().adverts}
          onChange={(e) => setF({ ...f(), adverts: e.currentTarget.checked })} /> Ad break at a quarter to each hour</label>
        <label class="flex items-center gap-2"><input type="checkbox" class="toggle toggle-primary toggle-sm" checked={f().top_of_hour}
          onChange={(e) => setF({ ...f(), top_of_hour: e.currentTarget.checked })} /> DJ's top-of-the-hour bit</label>
        <label class="flex items-center gap-2" title={props.station.playout === 'timeline' ? 'Rendered ahead between every two items; 0: no crossfade'
          : 'Applies once the station plays from the timeline; Liquidsoap crossfades 2 s'}>Crossfade
          <input type="number" min="0" max="10" step="0.5" class="input input-sm input-bordered w-20" value={f().crossfade}
            onChange={(e) => setF({ ...f(), crossfade: Math.max(0, Math.min(10, Number(e.currentTarget.value) || 0)) })} /> s</label>
        <label class="flex items-center gap-2">No song repeats within
          <input type="number" min="1" max="168" class="input input-sm input-bordered w-20" value={f().no_repeat_hours}
            onChange={(e) => setF({ ...f(), no_repeat_hours: Number(e.currentTarget.value) || 24 })} /> hours</label>
      </div>
      <div class="flex gap-2 sm:col-span-2">
        <button class="btn btn-sm btn-primary" disabled={a.busy()}>Save station</button>
        <button type="button" class="btn btn-sm btn-ghost" onClick={reset}>Undo changes</button>
      </div>
      <ErrorLine error={a.error} />
    </form>
  );
}

const FOR = [['', 'until handed back'], ['60', 'for an hour'], ['120', 'for two hours'], ['programme', 'until the programme ends']];

/* The console's mode switch: what plays now and why, and an override that wins over the programme and the station
   until it is handed back or runs out. Voice and live need an input (Studio C); until then they fall back. */
function ModeConsole(props: { station: Station; onSaved: (s: Station) => void }) {
  const [length, setLength] = createSignal('');
  const a = useAction();
  const m = () => props.station.mode_now;
  const noVoice = () => (props.station.dj ? `${props.station.dj.display_name} has no voice yet` : 'the station has no host');
  const why = () => ({ takedown: 'the station is in maintenance', override: 'the console override', live: 'a live source',
    programme: `the programme ${props.station.on_air.name}`, station: "the station's default" })[m().source];
  const until = () => {
    const v = length();
    if (v === 'programme') return props.station.on_air.until;
    return v ? new Date(Date.now() + Number(v) * 60_000).toISOString().slice(0, 19) : null;
  };
  const set = (mode: Mode) => a.run(async () => props.onSaved(await api<Station>(`/radio/stations/${props.station.id}/mode`,
    { method: 'PUT', json: { mode, until: until() } })));
  const handBack = () => a.run(async () => props.onSaved(await api<Station>(`/radio/stations/${props.station.id}/mode`, { method: 'DELETE' })));
  return (
    <div class="flex flex-col gap-3">
      <p class="text-sm">
        <Show when={m().mode} fallback={<><b>Taken down</b>: a holding loop plays ({why()}).</>}>
          Playing <b>{SHORT[m().mode!]}</b>, from {why()}
          <Show when={m().until}> until {new Date(m().until + 'Z').toLocaleTimeString('en-GB', { timeZone: props.station.timezone, hour: '2-digit', minute: '2-digit' })}</Show>.
          <Show when={m().wanted !== m().mode}> <span class="text-ink-3">{SHORT[m().wanted!]} was asked for but cannot play
            {m().wanted === 'dj' ? ` (${noVoice()})` : ' (no input yet)'}.</span></Show>
        </Show>
        <Show when={props.station.override && m().source !== 'override' && m().source !== 'takedown'}>
          {' '}<span class="text-ink-3">The override ({SHORT[props.station.override!.mode]}) is set but cannot play
            {props.station.override!.mode === 'dj' ? ` (${noVoice()})` : ' (no input yet)'}.</span></Show>
      </p>
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-sm text-ink-3">Override:</span>
        <For each={Object.keys(MODE) as Mode[]}>
          {(k) => <button class={`btn btn-sm ${props.station.override?.mode === k ? 'btn-primary' : 'btn-ghost'}`} disabled={a.busy()}
            onClick={() => set(k)} title={MODE[k]}>{SHORT[k]}</button>}
        </For>
        <select class="select select-bordered select-sm" value={length()} onChange={(e) => setLength(e.currentTarget.value)} aria-label="How long">
          <For each={FOR.filter(([k]) => k !== 'programme' || props.station.on_air.until)}>{([k, l]) => <option value={k}>{l}</option>}</For>
        </select>
        <Show when={props.station.override}>
          <button class="btn btn-sm" disabled={a.busy()} onClick={handBack}>Hand back</button>
        </Show>
      </div>
      <ErrorLine error={a.error} />
    </div>
  );
}

function ProgrammeEditor(props: { station: Station; programme: Programme; onSaved: (s: Station) => void; onRemoved: (s: Station) => void }) {
  const [f, setF] = createSignal({ name: '', description: '', brief: '', talk: 'normal', mode: '', source: 'mix' });
  const a = useAction();
  const p = () => props.programme;
  const isBase = () => p().slug === 'default';
  const base = () => props.station.programmes.find((x) => x.slug === 'default')!;
  const ownMix = () => p().genre_mix.length > 0;
  const reset = () => setF({ name: p().name, description: p().description ?? '', brief: p().brief ?? '', talk: p().talk, mode: p().mode ?? '',
    source: p().source });
  createEffect(reset);

  const put = (genre_mix: GenreWeight[], constraints: Record<string, any> | null) => {
    const v = f();
    return api<Station>(`/radio/stations/${props.station.id}/programmes/${p().slug}`, { method: 'PUT',
      json: { name: v.name, description: v.description.trim() || null, brief: v.brief.trim() || null, talk: v.talk, mode: v.mode || null,
        source: v.source, genre_mix, constraints } });
  };
  const saveDetails = (e: Event) => { e.preventDefault(); a.run(async () => props.onSaved(await put(p().genre_mix, p().constraints))); };
  // a programme whose mix equals the base format's keeps following the base format
  const saveMix = async (mix: GenreWeight[], c: Record<string, any>) =>
    props.onSaved(await put(!isBase() && sameMix(mix, base().genre_mix) ? [] : mix, Object.keys(c).length ? c : null));
  const remove = () => a.run(async () => props.onRemoved(await api<Station>(`/radio/stations/${props.station.id}/programmes/${p().slug}`, { method: 'DELETE' })));

  return (
    <div class="flex flex-col gap-6">
      <form class="grid gap-3 sm:grid-cols-2" onSubmit={saveDetails}>
        <label class="form-control"><span class="label-text font-semibold">Name (public)</span>
          <input class="input input-bordered" required value={f().name} onInput={(e) => setF({ ...f(), name: e.currentTarget.value })} /></label>
        <label class="form-control"><span class="label-text font-semibold">DJ talk</span>
          <select class="select select-bordered" value={f().talk} onChange={(e) => setF({ ...f(), talk: e.currentTarget.value })}>
            <For each={Object.entries(TALK)}>{([k, l]) => <option value={k}>{l}</option>}</For>
          </select></label>
        <label class="form-control"><span class="label-text font-semibold">Mode</span>
          <select class="select select-bordered" value={f().mode} onChange={(e) => setF({ ...f(), mode: e.currentTarget.value })}>
            <option value="">The station's ({SHORT[props.station.mode]})</option>
            <For each={Object.entries(MODE)}>{([k, l]) => <option value={k}>{l}</option>}</For>
          </select></label>
        <label class="form-control sm:col-span-2"><span class="label-text font-semibold">Description (public, in the schedule)</span>
          <input class="input input-bordered" maxLength={500} value={f().description} onInput={(e) => setF({ ...f(), description: e.currentTarget.value })} /></label>
        <label class="form-control sm:col-span-2"><span class="label-text font-semibold">Brief for the DJ (never shown)</span>
          <textarea class="textarea textarea-bordered text-sm" rows="3" maxLength={4000} value={f().brief}
            placeholder="What this programme is about, what the DJ does differently, running bits for this slot."
            onInput={(e) => setF({ ...f(), brief: e.currentTarget.value })} /></label>
        <label class="form-control"><span class="label-text font-semibold">Plays</span>
          <select class="select select-bordered" value={f().source} disabled={isBase()} onChange={(e) => setF({ ...f(), source: e.currentTarget.value })}>
            <For each={Object.entries(SOURCE)}>{([k, l]) => <option value={k}>{l}</option>}</For>
          </select></label>
        <div class="flex items-end gap-2">
          <button class="btn btn-sm btn-primary" disabled={a.busy()}>Save details</button>
          <button type="button" class="btn btn-sm btn-ghost" onClick={reset}>Undo changes</button>
        </div>
        <ErrorLine error={a.error} />
      </form>

      <div>
        <h3 class="th mb-1">Music</h3>
        <p class="mb-3 text-sm text-ink-3">
          <Show when={isBase()} fallback={<>
            <Show when={ownMix()} fallback={<>Plays the {base().name} mix. Change the mix here to give it its own.</>}>
              Has its own mix. Set it back to the {base().name} mix to follow that again.</Show>
            <Show when={f().source !== 'mix'}> The songs come from the choice above; the mix sets which genres they may be.</Show>
          </>}>Plays whenever the grid has no programme on, and is the mix programmes without their own follow.</Show>
        </p>
        <MixEditor admin={true} mix={ownMix() ? p().genre_mix : base().genre_mix} constraints={p().constraints ?? {}}
          previewPath={`/radio/stations/${props.station.id}/preview`} onSave={saveMix} />
      </div>

      <Show when={!isBase()}>
        <div class="border-t border-line pt-3">
          <ConfirmButton label={`Remove ${p().name}`} busy={a.busy()} confirm="Remove programme" onConfirm={remove}
            warning={`${p().name} and its ${p().hours_per_week} h a week in the grid go; the base format plays there instead.`} />
        </div>
      </Show>
    </div>
  );
}

export default function StationPage() {
  const params = useParams();
  const [loaded] = createResource(() => params.id, (id) => api<Station>(`/radio/stations/${id}`));
  const [djs] = createResource(() => api<DJ[]>('/radio/djs'));
  const [station, setStation] = createSignal<Station | null>(null);
  const [sel, setSel] = createSignal('default');
  const [newName, setNewName] = createSignal('');
  const a = useAction();
  createEffect(() => { if (loaded()) setStation(loaded()!); });
  const prog = () => station()?.programmes.find((p) => p.slug === sel()) ?? station()?.programmes[0];

  const addProgramme = (e: Event) => {
    e.preventDefault();
    const slug = slugOf(newName());
    if (!slug) return;
    a.run(async () => {
      const s = await api<Station>(`/radio/stations/${station()!.id}/programmes/${slug}`, { method: 'PUT', json: { name: newName().trim() } });
      setStation(s); setSel(slug); setNewName('');
    });
  };

  return (
    <Show when={station()} fallback={<p class="text-ink-3">{loaded.error ? loaded.error.message : 'Loading…'}</p>}>
      {(s) => (
        <>
          <PageHeader crumbs={[{ href: '/radio', label: 'Radio' }, { label: s().name }]} title={s().name}
            actions={<StatusPill tone={s().status === 'on_air' ? 'ok' : 'off'}>{s().status === 'on_air' ? 'On air' : 'Off air'}</StatusPill>} />
          <p class="-mt-3 mb-6 text-ink-2">{s().dj ? <>with <b>{s().dj!.display_name}</b></> : 'no host'} · now {s().on_air.name} <span class="text-ink-3">{untilLabel(s())}</span>
            <Show when={s().mount}> · <span class="mono text-sm">play.scai.fm{s().mount}</span>
              <span class="text-sm text-ink-3"> ({s().playout === 'timeline' ? 'timeline' : 'Liquidsoap'})</span></Show>
            <Show when={s().status === 'on_air'}> · {s().listeners} listening</Show></p>

          <section class="card-z mb-6">
            <h2 class="th mb-3">Mode</h2>
            <ModeConsole station={s()} onSaved={setStation} />
          </section>

          <section class="card-z mb-6">
            <h2 class="th mb-3">Station</h2>
            <StationDetails station={s()} djs={djs() ?? (s().dj ? [s().dj!] : [])} onSaved={setStation} />
          </section>

          <section class="card-z mb-6">
            <div class="mb-3 flex flex-wrap items-center gap-2">
              <h2 class="th mr-2">Programmes</h2>
              <For each={s().programmes}>
                {(p) => (
                  <button class={`btn btn-sm ${sel() === p.slug ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setSel(p.slug)}>
                    <Show when={p.slug !== 'default'}><span class="h-2.5 w-2.5 rounded-full" style={{ background: colourOf(s(), p.slug) }} aria-hidden="true" /></Show>
                    {p.slug === 'default' ? `${p.name} (base)` : p.name}
                    <span class="mono text-xs opacity-70">{p.hours_per_week} h</span>
                  </button>
                )}
              </For>
              <form class="ml-auto flex items-center gap-1" onSubmit={addProgramme}>
                <input class="input input-sm input-bordered w-44" placeholder="New programme…" value={newName()}
                  onInput={(e) => setNewName(e.currentTarget.value)} aria-label="New programme name" />
                <button class="btn btn-ghost btn-sm" disabled={!slugOf(newName()) || a.busy()}>Add</button>
              </form>
            </div>
            <ErrorLine error={a.error} />
            <Show when={prog()} keyed>
              {(p) => <ProgrammeEditor station={s()} programme={p} onSaved={setStation}
                onRemoved={(st) => { setSel('default'); setStation(st); }} />}
            </Show>
          </section>

          <section class="card-z mb-6">
            <h2 class="th mb-3">Running order</h2>
            <RunningOrder station={s()} onSaved={setStation} />
          </section>

          <Show when={s().programmes.some((p) => p.talk !== 'none')}>
            <section class="card-z mb-6">
              <h2 class="th mb-3">Shouts</h2>
              <Shouts station={s()} />
            </section>
          </Show>

          <section class="card-z">
            <h2 class="th mb-3">Weekly grid</h2>
            <GridEditor station={s()} onSaved={setStation} />
          </section>
        </>
      )}
    </Show>
  );
}
